import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Instagram, Facebook, Linkedin } from "lucide-react";
import { LOGOS, BRAND, FORMS, SOCIAL_LINKS } from "@/constants/brand";
import { useTranslation } from "@/i18n/LanguageContext";
import LanguageToggle from "@/components/LanguageToggle";

// TikTok icon (lucide-react doesn't ship one - inline SVG)
function TikTokIcon({ size = 16 }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
        >
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.71a8.16 8.16 0 0 0 4.77 1.52V7.79a4.85 4.85 0 0 1-1.84-1.1Z" />
        </svg>
    );
}

const socialItemsData = [
    { key: "instagram", href: SOCIAL_LINKS.instagram, label: "Instagram", Icon: Instagram, testId: "nav-social-instagram" },
    { key: "facebook", href: SOCIAL_LINKS.facebook, label: "Facebook", Icon: Facebook, testId: "nav-social-facebook" },
    { key: "tiktok", href: SOCIAL_LINKS.tiktok, label: "TikTok", Icon: TikTokIcon, testId: "nav-social-tiktok" },
    { key: "linkedin", href: SOCIAL_LINKS.linkedin, label: "LinkedIn", Icon: Linkedin, testId: "nav-social-linkedin" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();

    const navLinks = [
        { to: "/", label: t("header.nav.home"), testKey: "home" },
        { to: "/about", label: t("header.nav.about"), testKey: "about" },
        { to: "/for-homeowners", label: t("header.nav.forHomeowners"), testKey: "for-homeowners" },
        { to: "/for-service-providers", label: t("header.nav.forProviders"), testKey: "for-service-providers" },
        { to: "/contact", label: t("header.nav.contact"), testKey: "contact" },
    ];

    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-50 w-full bg-cream/85 backdrop-blur-xl border-b border-border/60"
        >
            <div className="container-x flex items-center justify-between h-20">
                <Link
                    to="/"
                    data-testid="header-logo-link"
                    onClick={() => setOpen(false)}
                    className="group flex flex-col items-center gap-1 leading-none"
                    aria-label={`${BRAND.name} home`}
                >
                    <span className="font-heading text-xl sm:text-2xl text-ink tracking-tight leading-none whitespace-nowrap">
                        {BRAND.name}
                    </span>
                    <img
                        src={LOGOS.markDark}
                        alt=""
                        aria-hidden="true"
                        className="h-7 sm:h-8 w-auto select-none"
                        draggable="false"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-9 ml-8 xl:ml-16">
                    {navLinks.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-${l.testKey}`}
                            className={({ isActive }) =>
                                `text-sm font-medium tracking-wide transition-colors ${
                                    isActive
                                        ? "text-ink"
                                        : "text-ink-soft hover:text-ink"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-5">
                    <div className="ml-2 lg:ml-4 pl-2 lg:pl-4 border-l border-border/60">
                        <LanguageToggle variant="header" />
                    </div>
                    <div className="flex items-center gap-1" aria-label="Social media">
                        {socialItemsData.map(({ href, label, Icon, testId, key }) => (
                            <a
                                key={key}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={label}
                                data-testid={testId}
                                className="w-8 h-8 rounded-full flex items-center justify-center text-ink-soft hover:text-ink hover:bg-ink/5 transition-colors"
                            >
                                <Icon size={16} />
                            </a>
                        ))}
                    </div>
                    <a
                        href={FORMS.homeownerCommunity}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="header-waitlist-cta"
                        className="rounded-full bg-ink text-cream px-5 py-2.5 text-sm font-semibold hover:bg-ink-soft transition-colors duration-300"
                    >
                        {t("header.cta")}
                    </a>
                </div>

                <button
                    data-testid="mobile-menu-toggle"
                    aria-label="Toggle menu"
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden p-2 -mr-2 text-ink"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu-panel"
                    className="lg:hidden border-t border-border/60 bg-cream/95 backdrop-blur-xl"
                >
                    <div className="container-x py-6 flex flex-col gap-1">
                        {navLinks.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${l.testKey}`}
                                className={({ isActive }) =>
                                    `py-3 text-base font-medium border-b border-border/40 ${
                                        isActive ? "text-ink" : "text-ink-soft"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}

                        <div className="mt-5 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2" aria-label="Social media">
                                {socialItemsData.map(({ href, label, Icon, testId, key }) => (
                                    <a
                                        key={key}
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={label}
                                        data-testid={`mobile-${testId}`}
                                        className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-ink hover:bg-ink hover:text-cream transition-colors"
                                    >
                                        <Icon size={18} />
                                    </a>
                                ))}
                            </div>
                            <LanguageToggle variant="mobile" />
                        </div>

                        <a
                            href={FORMS.homeownerCommunity}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-waitlist-cta"
                            className="mt-5 rounded-full bg-ink text-cream px-5 py-3 text-sm font-semibold text-center"
                        >
                            {t("header.cta")}
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
